import React, { Component } from 'react';
import { navigate } from 'gatsby';
import { getMarketoCookie } from '../utils/marketo';
import { readCookie, parseCookieToObject } from '../utils/cookie';
import Link from '../utils/link';
import { getSubscribedCookie, setSubscribedCookie } from "../utils/subscribe-popup";

function encode(data) {
  return Object.keys(data)
    .map(key => encodeURIComponent(key) + "=" + encodeURIComponent(data[key]))
    .join("&");
}

// --- Spam filter (loosened for subscribe form) ---
const RAND_TOKEN = /(?=.*[A-Z])(?=.*[a-z])(?=.*\d)[A-Za-z0-9]{10,}/g;
const LONG_CONSONANTS = /[bcdfghjklmnpqrstvwxyz]{6,}/i; // 6+ instead of 5+

function shannonEntropy(s) {
  if (!s) return 0;
  const t = s.replace(/\s+/g, "");
  const freq = {};
  for (const c of t) freq[c] = (freq[c] || 0) + 1;
  const n = t.length || 1;
  return Object.values(freq).reduce((H, v) => {
    const p = v / n;
    return H - p * Math.log2(p);
  }, 0);
}

function looksRandomish(s) {
  if (!s) return false;
  const tokens = s.match(RAND_TOKEN) || [];
  const ent = shannonEntropy(s);
  const hasFewSpaces = s.trim().split(/\s+/).length <= 2;
  const alphaRatio = s.replace(/[^A-Za-z]/g, "").length / Math.max(1, s.length);

  // Slightly stricter than CommonForm: require either a token OR high entropy,
  // but only applied to names in this form.
  return (
    tokens.length >= 1 ||
    (ent > 4.2 && hasFewSpaces && alphaRatio > 0.7)
  );
}

// Email basics
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;

/**
 * Loosened validation for subscribe form.
 * - Required: firstname, lastname, email, subscribe_type (if disclaimer)
 * - Format: email must look like an email
 * - Spam: only check firstname/lastname (NOT email)
 */
function validateSubscribe({ firstname, lastname, email, subscribe_type }, hasDisclaimer) {
  const fieldErrors = {};
  const spamReasons = [];

  // Requireds
  if (!firstname?.trim()) fieldErrors.firstname = "First name is required.";
  if (!lastname?.trim()) fieldErrors.lastname = "Last name is required.";
  if (!email?.trim()) fieldErrors.email = "Email is required.";

  if (hasDisclaimer && !subscribe_type) {
    fieldErrors.subscribe_type = "Please make a selection.";
  }

  // Email format
  if (email && !EMAIL_RE.test(email)) {
    fieldErrors.email = "Please enter a valid email address.";
  }

  // Spam checks – **names only**, we do NOT scrutinise the email
  const textish = { firstname, lastname };

  Object.entries(textish).forEach(([key, val]) => {
    if (!val || typeof val !== "string") return;

    if (looksRandomish(val)) {
      spamReasons.push(`${key}: random-like`);
      if (!fieldErrors[key]) {
        fieldErrors[key] = "This looks a bit random. Please rephrase.";
      }
    }

    if (LONG_CONSONANTS.test(val)) {
      spamReasons.push(`${key}: too many consonants`);
      if (!fieldErrors[key]) {
        fieldErrors[key] = "Too many consonants in a row. Please rephrase.";
      }
    }
  });

  // Very conservative same-value rule: only if BOTH names are
  // long(ish) and literally identical. Email is NOT part of this.
  const fn = (firstname || "").trim();
  const ln = (lastname || "").trim();
  if (fn.length >= 8 && ln.length >= 8 && fn.toLowerCase() === ln.toLowerCase()) {
    spamReasons.push("firstname-lastname-identical");
    if (!fieldErrors.firstname && !fieldErrors.lastname) {
      fieldErrors.__form =
        "Your first and last name are identical – please check if this is correct.";
    }
  }

  return {
    ok: Object.keys(fieldErrors).length === 0,
    fieldErrors,
    reasons: spamReasons,
  };
}

class SubscribeForm extends Component {

  state = {
    firstname: '',
    lastname: '',
    email: '',
    is_website: true,
    subscribe_type: 'Residential',
    form_name: this.props.disclaimer ? 'Subscribe-popup' : 'Subscribe',
    success: false,
    isSubmitting: false,
    cookie: '',
    fieldErrors: {},
  }

  constructor(props) {
    super(props);
    this.form = React.createRef();
  }

  componentDidMount() {
    let cookie = getMarketoCookie();
    this.setState({ cookie });
  }

  handleChange = e => {
    const { name, value, checked, type } = e.target;

    this.setState(prevState => {
      let nextValue;

      if (type === 'checkbox') {
        if (checked) {
          nextValue = prevState[name]
            ? `${prevState[name]},${value}`
            : value;
        } else {
          nextValue = prevState[name]
            ? prevState[name]
                .split(',')
                .filter(item => item !== value)
                .join(',')
            : '';
        }
      } else {
        nextValue = value;
      }

      const nextFieldErrors = { ...prevState.fieldErrors };
      delete nextFieldErrors[name];
      delete nextFieldErrors.__form;

      return {
        [name]: nextValue,
        fieldErrors: nextFieldErrors,
      };
    });
  }

  handleSubmit = e => {
    e.preventDefault();

    const { ok, fieldErrors } = validateSubscribe(
      this.state,
      !!this.props.disclaimer
    );

    if (!ok) {
      this.setState({ fieldErrors });
      return false;
    }

    if (this.form.current && !this.form.current.checkValidity()) {
      return false;
    }

    // Disable the submit button
    this.setState({ isSubmitting: true });

    let message = `
      Subscribe Form Submission
    `;

    // Build payload state without UI-only fields
    const rawState = this.state;
    let state = { ...rawState };
    delete state.fieldErrors;
    delete state.isSubmitting;
    delete state.success;

    for (let key in state) {
      if (
        typeof state[key] === 'object' &&
        state[key] !== null &&
        state[key].hasOwnProperty('value')
      ) {
        state[key] = state[key].value;
      }
    }

    if (this.props.disclaimer) this.setState({ success: true });

    let utmFromCookie = readCookie('utmParameters');
    utmFromCookie = parseCookieToObject(utmFromCookie);

    return fetch(`https://kollosche.codedrips.com/wp-json/team/v1/enquiry`, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: encode({
        message,
        ...utmFromCookie,
        ...state
      })
    })
      .then(res => res.json())
      .then((res) => {
        if (res?.response?.status === 'success') {
          setSubscribedCookie();
          if (this.props.setPathFound) this.props.setPathFound(false);
          if (this.props.disclaimer) {
            this.props.setSubscribePopup(false);
          } else {
            navigate('/news/thank-you/');
          }
        }
        if (res?.response?.errors) {
          this.setState({
            result: 'fail',
            msg: res.response.errors.map(err =>
              err.detail.split('\n').map((el, i) => <div key={i}>{el}</div>)
            )
          });
        }
      })
      .catch(error => this.setState({ result: 'fail', msg: error }))
      .finally(() => {
        // Re-enable the submit button after the request completes
        this.setState({ isSubmitting: false });
      });
  }

  render() {
    const { subscribe_type, fieldErrors } = this.state;

    let props = {
      ref: this.form,
      name: 'subscribe',
      className: 'form',
      onSubmit: this.handleSubmit,
    };

    if (this.state.success && this.props.disclaimer) return (
      <div className='form form--success'>
        <p>Thank you for subscribing.</p>
      </div>
    );

    return (
      <form {...props}>

        {fieldErrors.__form && (
          <div className='form__error' style={{ marginBottom: 16 }}>
            {fieldErrors.__form}
          </div>
        )}

        {this.props.disclaimer &&
          <div className='form__row form__row--checkboxes'>

            <label htmlFor='sales-appraisal'>Hear more about:</label>

            <div className='form__checkbox-group'>

              <div className='form__checkbox'>
                <input
                  type='checkbox'
                  id='residential-type'
                  name="subscribe_type"
                  value="Residential"
                  checked={subscribe_type.includes('Residential')}
                  onChange={this.handleChange}
                />
                <label htmlFor='residential-type'>Residential</label>
              </div>

              <div className='form__checkbox'>
                <input
                  type='checkbox'
                  id='commercial-type'
                  name="subscribe_type"
                  value="Commercial"
                  checked={subscribe_type.includes('Commercial')}
                  onChange={this.handleChange}
                />
                <label htmlFor='commercial-type'>Commercial</label>
              </div>

            </div>

            {fieldErrors.subscribe_type && (
              <p className='form__error'>{fieldErrors.subscribe_type}</p>
            )}

          </div>
        }

        <div className='form__row'>
          <label htmlFor='first-name-field'> First Name </label>
          <input
            type='text'
            name='firstname'
            id='first-name-field'
            className='form__input'
            placeholder={this.props.disclaimer ? 'First Name' : ''}
            onChange={this.handleChange}
            required
          />
          {fieldErrors.firstname && (
            <p className='form__error'>{fieldErrors.firstname}</p>
          )}
        </div>

        <div className='form__row'>
          <label htmlFor='last-name-field'> Last Name </label>
          <input
            type='text'
            name='lastname'
            id='last-name-field'
            className='form__input'
            placeholder={this.props.disclaimer ? 'Last Name' : ''}
            onChange={this.handleChange}
            required
          />
          {fieldErrors.lastname && (
            <p className='form__error'>{fieldErrors.lastname}</p>
          )}
        </div>

        <div className='form__row'>
          <label htmlFor='email-field'> Email address </label>
          <input
            type='text'
            name='email'
            id='email-field'
            className='form__input'
            placeholder={this.props.disclaimer ? 'Email Address' : ''}
            onChange={this.handleChange}
            required
          />
          {fieldErrors.email && (
            <p className='form__error'>{fieldErrors.email}</p>
          )}
        </div>

        <div className='form__row form__row--submit'>

          {this.props.disclaimer &&
            <p>
              By subscribing you consent to receive marketing communications from Kollosche as per our{' '}
              <Link to='/privacy-policy/' target='_blank' rel='noopener noreferrer'>
                Privacy Policy
              </Link>
            </p>
          }

          <button
            type='submit'
            className='btn btn--border'
            disabled={this.state.isSubmitting}
          >
            Subscribe
          </button>

        </div>

      </form>
    );
  }
}

export default SubscribeForm;
